import { useNavigate } from 'react-router-dom';
import RankBadge from './RankBadge';
import InitialsAvatar from './InitialsAvatar';
import { formatAppearances, formatPlayerGPG } from '../utils/formatters';

// GPG value that fills the efficiency meter
const GPG_METER_MAX = 1.5;

export default function PlayerTable({ players = [], limit, showRank = true, startRank = 1 }) {
  const navigate = useNavigate();
  const displayed = limit ? players.slice(0, limit) : players;

  if (displayed.length === 0) {
    return (
      <div className="p-12 text-center">
        <span className="material-symbols-outlined text-4xl text-outline mb-2">search_off</span>
        <p className="text-on-surface-variant text-sm">No players found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-separate border-spacing-y-0.5">
        <thead>
          <tr className="font-label text-[10px] font-semibold text-on-surface-variant uppercase tracking-[0.12em]">
            {showRank && <th className="px-3 py-2.5 sm:px-4 border-b border-outline-variant/30">Rank</th>}
            <th className="px-3 py-2.5 sm:px-4 border-b border-outline-variant/30">Player</th>
            <th className="px-3 py-2.5 sm:px-4 border-b border-outline-variant/30">Club</th>
            <th className="px-3 py-2.5 sm:px-4 text-center border-b border-outline-variant/30">Goals</th>
            <th className="px-3 py-2.5 sm:px-4 text-center border-b border-outline-variant/30">Apps</th>
            <th className="px-3 py-2.5 sm:px-4 text-right border-b border-outline-variant/30">GPG</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map((player, i) => {
            const rank = startRank + i;
            const name = player.player_name || player.name;
            const club = player.club_name || player.club;
            const clubUrl = player.club_website || null;
            const goals = player.total_goals || player.goals || 0;
            const apps = formatAppearances(player);
            const gpg = formatPlayerGPG(player);
            // Meter reuses the displayed GPG so bar and number always agree
            const gpgValue = gpg === '—' ? NaN : Number(gpg);
            const gpgWidth = Number.isFinite(gpgValue)
              ? Math.min((gpgValue / GPG_METER_MAX) * 100, 100)
              : 0;

            return (
              <tr
                key={`${name}-${club}-${rank}`}
                onClick={() => navigate(`/players/${encodeURIComponent(name)}`)}
                className="group hover:bg-surface-container-high transition-colors duration-150 cursor-pointer active:scale-[0.995]"
              >
                {showRank && (
                  <td className="px-3 py-3.5 sm:px-4 rounded-l-lg relative">
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-7 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    <RankBadge position={rank} />
                  </td>
                )}
                <td className={`px-3 py-3.5 sm:px-4 ${!showRank ? 'rounded-l-lg' : ''}`}>
                  <div className="flex items-center gap-3">
                    <InitialsAvatar name={name} clubName={club} size="sm" />
                    <span className="font-semibold text-on-surface text-sm">{name}</span>
                  </div>
                </td>
                <td className="px-3 py-3.5 sm:px-4 text-on-surface-variant text-sm">
                  {clubUrl ? (
                    <a
                      href={clubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="hover:text-primary transition-colors underline decoration-outline-variant/30 underline-offset-2 hover:decoration-primary"
                    >
                      {club}
                    </a>
                  ) : club}
                </td>
                <td className="px-3 py-3.5 sm:px-4 text-center font-headline font-bold text-on-surface tabular-nums text-base">{goals}</td>
                <td className="px-3 py-3.5 sm:px-4 text-center text-on-surface-variant tabular-nums text-sm">{apps}</td>
                <td className="px-3 py-3.5 sm:px-4 rounded-r-lg">
                  <div className="flex items-center justify-end gap-2">
                    <span className="hidden sm:block h-1 w-14 rounded-full bg-surface-container-highest overflow-hidden">
                      <span className="block h-full rounded-full bg-secondary" style={{ width: `${gpgWidth}%` }} />
                    </span>
                    <span className="w-9 text-right font-headline font-bold text-secondary tabular-nums text-sm">{gpg}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
